import { type Failure, RequestFailed, malformed, readJson, request } from './request'

/**
 * Links between two pieces of content, as the service describes them.
 *
 * **One address family for every pair of kinds there will ever be.** There is no "documents of an
 * appointment" and no "appointments of a document": a link is a kind and an id on each side, so
 * linking a card to a lecture and linking a note to a document are the same request with other words
 * in it. A screen that can do one can do all of them, and a further kind of content costs nothing
 * here.
 *
 * A link is **one fact seen from both ends**. It is made once and read from either side; there is no
 * direction anywhere in these shapes, because there is none in the thing.
 */

/** What can be an end of a link. Deliberately not every kind of content — see the service. */
export type LinkedKind = 'DOCUMENT' | 'NOTE' | 'TASK' | 'FLASHCARD' | 'APPOINTMENT'

/** One end of a link: what it is, what it is called, and whether it is still there. */
export interface Linked {
  readonly linkId: string
  readonly kind: LinkedKind
  readonly id: string
  /** Read now and not copied when the link was made, so a newer version reads correctly. */
  readonly title: string
  /** A second line: the folder a document lies in, the day an appointment is on. May be empty. */
  readonly detail: string
  /**
   * Whether nothing could describe this end any more.
   *
   * Rare — every area says when its content goes for good, and the links go with it — and shown
   * rather than hidden, because a listing quietly one row shorter than it should be is worse than a
   * hole with a way to clear it.
   */
  readonly gone: boolean
  readonly madeAt: string
}

const LINKS = '/api/links'

/** Everything linked to one piece of content, newest first. */
export async function fetchLinks(
  kind: LinkedKind,
  id: string,
  signal?: AbortSignal,
): Promise<Linked[]> {
  const where = `kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`
  const response = await request(`${LINKS}?${where}`, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a list of links.')
  }
  return payload as Linked[]
}

/**
 * Links two pieces of content. Saying it twice is that link, and nothing has gone wrong.
 *
 * The answer describes the **other** end as seen from the first, which is what the screen that asked
 * redraws itself from.
 */
export async function link(
  a: { readonly kind: LinkedKind; readonly id: string },
  b: { readonly kind: LinkedKind; readonly id: string },
): Promise<Linked> {
  return (await change(LINKS, 'PUT', {
    aKind: a.kind,
    aId: a.id,
    bKind: b.kind,
    bId: b.id,
  })) as Linked
}

/** Unlinks them. **Neither of the two is changed**, which is why nothing asks first. */
export async function unlink(
  a: { readonly kind: LinkedKind; readonly id: string },
  b: { readonly kind: LinkedKind; readonly id: string },
): Promise<void> {
  const where =
    `aKind=${encodeURIComponent(a.kind)}&aId=${encodeURIComponent(a.id)}` +
    `&bKind=${encodeURIComponent(b.kind)}&bId=${encodeURIComponent(b.id)}`
  await change(`${LINKS}?${where}`, 'DELETE')
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-linked-content': 'That is not there any more, so it cannot be linked.',
  'link-to-itself': 'Something cannot be linked to itself.',
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    retryable: status >= 500,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
